import {
  MeasureReducer,
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class SchedulingEventsDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.SchedulingEvents;
  override readonly name = 'Scheduling History';
  override readonly description = `One result for each time an appointment is scheduled, rescheduled or cancelled.`;
  override readonly factTable = FactTables.schedulingEvent.latestEvent;
  override readonly rowItemName = 'Scheduling Event';

  override readonly queryableTimestamps = [
    { property: this.factTable.scheduledAt },
    { property: this.factTable.originalAppointmentDate },
    { property: this.factTable.newAppointmentDate },
  ];

  scopes = [
    TemplateScope.Appointment,
    TemplateScope.Patient,
    TemplateScope.TreatmentPlan,
  ];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.appointment,
    this.factTable.patient,
    this.factTable.treatmentPlan,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.scheduledByStaffer.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.reason.name),
      ReportBuilderGroups.toOption(this.factTable.schedulingEvent.eventType),
      ReportBuilderGroups.toOption(
        this.factTable.appointment.treatmentCategory
      ),
      ReportBuilderGroups.toOption(this.factTable.isFirstAppointment),
      ReportBuilderGroups.toOption(this.factTable.appointment.isBookedOnline),
      ReportBuilderGroups.toOption(this.factTable.movedFromStaffer.name),
      ReportBuilderGroups.toOption(this.factTable.movedToStaffer.name),
      ReportBuilderGroups.toOption(this.factTable.practitionerWasChanged),
      ReportBuilderGroups.toOption(this.factTable.practiceWasChanged),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.patient(this.factTable.patient),
    ReportBuilderGroups.appointment(this.factTable.appointment),
    ReportBuilderGroups.treatmentPlan(this.factTable.treatmentPlan),
    ReportBuilderGroups.schedulingEvent(this.factTable.schedulingEvent),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Scheduling Event', [
      this.factTable.schedulingEvent.eventType,
      this.factTable.reason.name,
      this.factTable.schedulingEvent.wasMovedToSameDay,
      this.factTable.schedulingEvent.wasFillingGap,
      this.factTable.practitionerWasChanged,
      this.factTable.practiceWasChanged,
      this.factTable.eventDurationChanged,
      this.factTable.eventMovedForward,
      this.factTable.eventMovedBackwards,
      this.factTable.schedulingEvent.scheduledAt,
      this.factTable.scheduledByStaffer.name,
      this.factTable.scheduledByPractice.name,
    ]),
    ReportBuilderColumns.toColumnGroup('Appointment', [
      this.factTable.treatmentCost,
      this.factTable.isFirstAppointment,
      ...ReportBuilderColumns.appointment(this.factTable.appointment),
    ]),
    ReportBuilderColumns.toColumnGroup('Moved From', [
      this.factTable.schedulingEvent.eventBeforeStartTime,
      this.factTable.schedulingEvent.eventBeforeEndTime,
      this.factTable.eventBeforeDurationMins,
      this.factTable.movedFromStaffer.name,
      this.factTable.movedFromPractice.name,
    ]),
    ReportBuilderColumns.toColumnGroup('Moved To', [
      this.factTable.schedulingEvent.eventAfterStartTime,
      this.factTable.schedulingEvent.eventAfterEndTime,
      this.factTable.eventAfterDurationMins,
      this.factTable.movedToStaffer.name,
      this.factTable.movedToPractice.name,
    ]),
    ReportBuilderColumns.toColumnGroup(
      'Patient',
      ReportBuilderColumns.patient(this.factTable.patient)
    ),
    ReportBuilderColumns.toColumnGroup(
      'Treatment Plan',
      ReportBuilderColumns.treatmentPlan(this.factTable.treatmentPlan)
    ),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.schedulingEvent.scheduledAt,
    this.factTable.schedulingEvent.eventType,
    this.factTable.reason.name,
    this.factTable.patient.name,
    this.factTable.isFirstAppointment,
    this.factTable.appointment.treatmentCategory,
    this.factTable.treatmentCost,
    this.factTable.schedulingEvent.eventBeforeStartTime,
    this.factTable.schedulingEvent.eventAfterStartTime,
    this.factTable.practitionerWasChanged,
    this.factTable.eventDurationChanged,
    this.factTable.scheduledByStaffer.name,
    this.factTable.schedulingEvent.wasMovedToSameDay,
    this.factTable.schedulingEvent.wasFillingGap,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.schedulingEvent.reason',
      this.factTable.reason.name,
      ChartFilterDefinition.row(
        'Reschedule/Cancellation Reasons',
        this.factTable.reason.name,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.schedulingEvent.eventType',
      this.factTable.schedulingEvent.eventType,
      ChartFilterDefinition.pie(
        'Scheduling Events by Type',
        this.factTable.schedulingEvent.eventType,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.wasFillingGap',
      this.factTable.schedulingEvent.wasFillingGap,
      ChartFilterDefinition.pie(
        'Moved to fill gap',
        this.factTable.schedulingEvent.wasFillingGap,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.wasMovedToSameDay',
      this.factTable.schedulingEvent.wasMovedToSameDay,
      ChartFilterDefinition.pie(
        'Moved to same day',
        this.factTable.schedulingEvent.wasMovedToSameDay,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practitionerWasChanged',
      this.factTable.practitionerWasChanged,
      ChartFilterDefinition.pie(
        'Practitioner Was Changed',
        this.factTable.practitionerWasChanged,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.daysBeforeOriginalBooking',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Average Days Before Original Appointment',
        this.factTable.brand.name,
        [
          toMeasureBuilderData(
            this.factTable.schedulingEvent.daysBeforeOriginalAppointmentDate,
            MeasureReducer.Average
          ),
        ]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.daysBeforeNewBooking',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Average Days Before New/Rescheduled Appointment',
        this.factTable.brand.name,
        [
          toMeasureBuilderData(
            this.factTable.schedulingEvent.daysBeforeNewAppointmentDate,
            MeasureReducer.Average
          ),
        ]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.treatmentCategory',
      this.factTable.appointment.treatmentCategory,
      ChartFilterDefinition.column(
        'Treatment Category',
        this.factTable.appointment.treatmentCategory,
        [toMeasureBuilderData(this.factTable.treatmentCost)],
        { rotateXAxisLabels: true },
        { groupLimit: 15, otherGroupLabel: 'Other', colourOverride: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.isFirstAppointment',
      this.factTable.isFirstAppointment,
      ChartFilterDefinition.pie(
        'Is First Appointment',
        this.factTable.isFirstAppointment,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ReportBuilderCharts.predefined(
      'appointment.isBookedOnline',
      this.factTable.appointment.isBookedOnline,
      ChartFilterDefinition.pie(
        'Is Booked Online',
        this.factTable.appointment.isBookedOnline,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
  ];
}
